<template>
  <Shell :label="label" :tooltip="tooltip" flush>
    <template v-slot:comp>
      <div
        data-testid="file-drop"
        class="relative w-full rounded border-2 border-dashed focus-within:border-primary"
        :class="highlighted ? 'border-primary bg-primary/5' : 'border-gray-300 bg-white'"
      >
        <input
          ref="inputElement"
          class="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
          v-bind="fileAttrs"
          type="file"
          :aria-label="label"
          @change="onFileChange"
          @focus="onFocus"
          @blur="onBlur"
          @dragenter.prevent="dragActive = true"
          @dragover.prevent="dragActive = true"
          @dragleave.prevent="dragActive = false"
          @drop.prevent.stop="onDrop"
          @paste.stop="onPaste"
        />
        <div class="pointer-events-none flex min-h-[7rem] flex-col items-center justify-center gap-2 px-3 py-4 text-center">
          <img v-if="previewUrl" :src="previewUrl" alt="" class="max-h-24 max-w-full object-contain" />
          <p class="text-sm text-gray-700">{{ fileLabel || hint }}</p>
          <p v-if="note" class="text-xs text-gray-500">{{ note }}</p>
          <p v-if="status" class="text-xs text-red-600">{{ status }}</p>
        </div>
        <div class="relative z-20 flex justify-center gap-3 pb-3">
          <button type="button" class="text-sm text-primary underline" @click.stop.prevent="pasteFromClipboard">Paste</button>
          <button v-if="fileLabel" type="button" class="text-sm text-gray-500 underline" @click.stop.prevent="clearFile">Remove</button>
        </div>
      </div>
    </template>
  </Shell>
</template>
<script setup="props">
import { computed, defineComponent, defineEmits, defineExpose, defineProps, onBeforeUnmount, onMounted, ref, useAttrs } from "vue";
import Shell from "./Shell.vue";
import {
    applyFiles,
    blurFileDropZone,
    clipboardFiles,
    createFileDropZoneId,
    dragHasFiles,
    dropHint,
    extensionForMime,
    fileSummary,
    filesFromList,
    focusFileDropZone,
    isPreviewable,
    mountFileDropZone,
    rejectionMessage,
    selectFiles,
    shouldArmForWindowDrag,
    shouldTakeDocumentPaste,
    unmountFileDropZone,
} from "./fileDrop.mjs";

defineComponent({
  inheritAttrs: false,
});

defineProps({
  label: String,
  tooltip: String,
});

const emit = defineEmits(["update:modelValue"]);
const attrs = useAttrs();
const inputElement = ref(null);
const fileAttrs = computed(() => {
  const rest = { ...attrs };
  delete rest.value;
  delete rest.modelValue;
  delete rest.type;
  delete rest.divClass;
  return rest;
});
const hint = computed(() => dropHint(attrs.accept || ""));
const dragActive = ref(false);
const armed = ref(false);
const highlighted = computed(() => dragActive.value || armed.value);
const fileLabel = ref("");
const previewUrl = ref("");
const status = ref("");
const note = ref("");
const zoneId = createFileDropZoneId();
let listening = false;

defineExpose({ inputElement });

function fieldOptions() {
  return {
    accept: attrs.accept || "",
    multiple: attrs.multiple === true || attrs.multiple === "",
  };
}

function blocked() {
  return attrs.disabled === true || attrs.disabled === "";
}

function syncFromInput() {
  const files = filesFromList(inputElement.value?.files);
  fileLabel.value = fileSummary(files);
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  const image = files.find((file) => isPreviewable(file));
  previewUrl.value = image ? URL.createObjectURL(image) : "";
  emit("update:modelValue", inputElement.value?.value ?? "");
}

function commit(incoming) {
  if (blocked()) return;
  const picked = selectFiles(incoming, fieldOptions());
  status.value = rejectionMessage(picked.rejected, picked.files.length);
  note.value = "";
  if (!picked.files.length) return;
  applyFiles(inputElement.value, picked.files);
  syncFromInput();
  inputElement.value?.dispatchEvent(new Event("input", { bubbles: true }));
}

function onFileChange() {
  status.value = "";
  note.value = "";
  dragActive.value = false;
  armed.value = false;
  syncFromInput();
}

function onDrop(event) {
  dragActive.value = false;
  armed.value = false;
  commit(filesFromList(event.dataTransfer?.files));
}

function onPaste(event) {
  const incoming = clipboardFiles(event.clipboardData);
  if (!incoming.length) return;
  event.preventDefault();
  commit(incoming);
}

function onDocumentPaste(event) {
  if (event.target === inputElement.value) return;
  if (!shouldTakeDocumentPaste(zoneId, event)) return;
  event.preventDefault();
  commit(clipboardFiles(event.clipboardData));
}

function onWindowDragOver(event) {
  if (blocked() || !dragHasFiles(event.dataTransfer) || !shouldArmForWindowDrag(zoneId)) return;
  event.preventDefault();
  armed.value = true;
}

function onWindowDragLeave(event) {
  if (event.relatedTarget) return;
  armed.value = false;
}

function onWindowDrop(event) {
  armed.value = false;
  if (blocked() || event.defaultPrevented || !shouldArmForWindowDrag(zoneId)) return;
  const incoming = filesFromList(event.dataTransfer?.files);
  if (!incoming.length) return;
  event.preventDefault();
  commit(incoming);
}

function onWindowDragEnd() {
  dragActive.value = false;
  armed.value = false;
}

function onFocus() {
  focusFileDropZone(zoneId);
}

function onBlur() {
  blurFileDropZone(zoneId);
}

function clearFile() {
  if (inputElement.value) inputElement.value.value = "";
  applyFiles(inputElement.value, []);
  status.value = "";
  syncFromInput();
}

async function pasteFromClipboard() {
  if (blocked()) return;
  status.value = "";
  inputElement.value?.focus();
  if (!navigator.clipboard?.read) {
    status.value = "Click the box and press ⌘V or Ctrl+V.";
    return;
  }
  try {
    const items = await navigator.clipboard.read();
    const files = [];
    for (const item of items) {
      const type = item.types.find((entry) => entry.startsWith("image/"));
      if (!type) continue;
      const blob = await item.getType(type);
      files.push(new File([blob], `pasted-image.${extensionForMime(type)}`, { type }));
    }
    if (!files.length) {
      status.value = "No image on the clipboard.";
      return;
    }
    commit(files);
  } catch {
    status.value = "Clipboard is blocked. Click the box and press ⌘V or Ctrl+V.";
  }
}

async function offerClipboardImage() {
  try {
    if (!navigator.permissions?.query || !navigator.clipboard?.read) return;
    const permission = await navigator.permissions.query({ name: "clipboard-read" });
    if (permission.state !== "granted" || fileLabel.value) return;
    const items = await navigator.clipboard.read();
    const hasImage = items.some((item) => item.types?.some((type) => type.startsWith("image/")));
    if (hasImage) note.value = "An image is on the clipboard.";
  } catch {
    // Browsers that have no clipboard-read permission stay quiet.
  }
}

function listen() {
  window.addEventListener("paste", onDocumentPaste);
  window.addEventListener("dragover", onWindowDragOver);
  window.addEventListener("dragleave", onWindowDragLeave);
  window.addEventListener("drop", onWindowDrop);
  window.addEventListener("dragend", onWindowDragEnd);
  listening = true;
}

function silence() {
  if (!listening) return;
  window.removeEventListener("paste", onDocumentPaste);
  window.removeEventListener("dragover", onWindowDragOver);
  window.removeEventListener("dragleave", onWindowDragLeave);
  window.removeEventListener("drop", onWindowDrop);
  window.removeEventListener("dragend", onWindowDragEnd);
  listening = false;
}

onMounted(() => {
  mountFileDropZone(zoneId);
  listen();
  offerClipboardImage();
});

onBeforeUnmount(() => {
  silence();
  unmountFileDropZone(zoneId);
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
});
</script>
